import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { Target } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

const thisMonth = () => new Date().toISOString().slice(0, 7);
const barColor = (pct) => (pct >= 100 ? "bg-emerald-500" : pct >= 50 ? "bg-amber-500" : "bg-brand-pink");

export const SalesTargets = () => {
  const { user } = useAuth();
  const isCEO = ["network_admin", "ceo"].includes(user.role);
  const [month] = useState(thisMonth());
  const [rows, setRows] = useState([]);
  const [draft, setDraft] = useState({});
  const [editing, setEditing] = useState(false);

  const load = useCallback(() => {
    api
      .get("/sales-targets", { params: { month } })
      .then(({ data }) => {
        setRows(data);
        setDraft(Object.fromEntries(data.map((r) => [r.user_id, String(r.goal || "")])));
      })
      .catch(() => {});
  }, [month]);
  useEffect(() => { load(); }, [load]);

  const save = async (uid) => {
    try {
      await api.put("/sales-targets", { user_id: uid, month, goal: Number(draft[uid] || 0) });
      toast.success("Target saved");
      load();
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
    }
  };

  if (!rows.length && !isCEO) return null;

  if (!isCEO) {
    const r = rows[0];
    return (
      <Card className="border-brand-line shadow-none" data-testid="my-sales-target">
        <CardHeader className="flex-row items-center gap-2 space-y-0 pb-2">
          <Target className="h-4 w-4 text-brand-pink" />
          <CardTitle className="text-base">My monthly sales target</CardTitle>
        </CardHeader>
        <CardContent>
          {r.goal > 0 ? (
            <>
              <div className="flex items-end justify-between">
                <span className="font-display text-3xl font-semibold text-brand-ink" data-testid="my-sales-achieved">
                  {r.achieved}
                </span>
                <span className="text-sm text-slate-500">of {r.goal} delivered jobs</span>
              </div>
              <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                <div className={`h-full ${barColor(r.pct)}`} style={{ width: `${Math.min(100, r.pct)}%` }} />
              </div>
              <p className="mt-2 text-xs font-semibold text-brand-ink" data-testid="my-sales-pct">
                {r.pct}% achieved
              </p>
            </>
          ) : (
            <p className="text-sm text-slate-500">No target set for this month yet.</p>
          )}
        </CardContent>
      </Card>
    );
  }

  return (
    <div data-testid="sales-targets-manage">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h3 className="flex items-center gap-2 font-display text-lg font-semibold text-brand-ink">
          <Target className="h-4 w-4 text-brand-pink" /> Salesperson job targets (delivered jobs)
        </h3>
        {rows.length > 0 && (
          <Button
            size="sm"
            variant={editing ? "default" : "outline"}
            data-testid="sales-targets-edit-toggle"
            onClick={() => setEditing((e) => !e)}
          >
            {editing ? "Done" : "Edit targets"}
          </Button>
        )}
      </div>
      {rows.length === 0 ? (
        <p className="text-sm text-slate-400">No Central Sales users found.</p>
      ) : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {rows.map((r) => (
            <Card key={r.user_id} className="border-brand-line shadow-none" data-testid={`sales-target-${r.user_id}`}>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">{r.name}</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end justify-between">
                  <span className="font-display text-2xl font-semibold text-brand-ink">{r.achieved}</span>
                  <span className="text-sm text-slate-500">of {r.goal || 0}</span>
                </div>
                <div className="mt-2 h-2 w-full overflow-hidden rounded-full bg-slate-100">
                  <div className={`h-full ${barColor(r.pct)}`} style={{ width: `${Math.min(100, r.pct)}%` }} />
                </div>
                <p className="mt-1 text-xs font-semibold text-brand-ink">{r.pct}% achieved</p>
                {editing ? (
                  <div className="mt-3 flex gap-2">
                    <Input
                      type="number"
                      min="0"
                      className="h-9"
                      data-testid={`sales-goal-input-${r.user_id}`}
                      value={draft[r.user_id] ?? ""}
                      placeholder="Set goal"
                      onChange={(e) => setDraft((d) => ({ ...d, [r.user_id]: e.target.value }))}
                    />
                    <Button size="sm" variant="outline" data-testid={`sales-goal-save-${r.user_id}`} onClick={() => save(r.user_id)}>
                      Save
                    </Button>
                  </div>
                ) : (
                  !r.goal && (
                    <p className="mt-2 text-xs text-amber-600" data-testid={`sales-no-goal-${r.user_id}`}>
                      No target set — tap "Edit targets"
                    </p>
                  )
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};
